import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/lbc5xhdz_Biovex%20Technologies%20logo%20design.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Projects', path: '/projects' },
  { label: 'Research', path: '/research' },
  { label: 'For Investors', path: '/investors' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4">
      <nav
        className={`w-full max-w-5xl rounded-full px-6 py-3 transition-colors duration-300 ${
          scrolled
            ? 'bg-black/70 backdrop-blur-xl border border-white/10'
            : 'bg-black/40 backdrop-blur-md border border-white/6'
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="bg-white/95 rounded-lg p-0.5 h-8 w-8 flex items-center justify-center overflow-hidden shrink-0">
              <img src={LOGO_URL} alt="Biovex Technologies" className="h-full w-full object-cover object-top" />
            </div>
            <span className="font-outfit font-bold text-white text-base tracking-wide">
              BIOVEX<span className="text-bvx-cyan">.</span>
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-inter-tight transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'bg-bvx-cyan/15 text-bvx-cyan font-medium'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="hidden md:block bg-bvx-cyan text-black font-bold font-outfit text-xs px-5 py-2 rounded-full hover:shadow-neon-cyan transition-shadow duration-300 tracking-wide"
          >
            Get In Touch
          </Link>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="navbar-mobile-toggle"
            className="md:hidden text-white/70 hover:text-white transition-colors duration-200 p-1"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className="flex flex-col gap-1 pt-3 pb-2 border-t border-white/8 mt-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className={`px-4 py-2 rounded-lg text-sm font-inter-tight transition-colors duration-200 ${
                      isActive(link.path)
                        ? 'bg-bvx-cyan/15 text-bvx-cyan font-medium'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta"
                  className="mt-2 bg-bvx-cyan text-black font-bold font-outfit text-sm px-5 py-2.5 rounded-full text-center hover:shadow-neon-cyan transition-shadow duration-300"
                >
                  Get In Touch
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
